import React from 'react';
import {
  FormControl,
  OutlinedInput,
  Typography,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Button,
  Checkbox,
  Stack,
  FormHelperText,
  CircularProgress,
} from '@mui/material';
import { useThemeColors } from '../../hooks/useThemeColors';

const labelStyle = {
  color: 'rgba(0, 0, 0, 0.87)',
  fontSize: '0.875rem',
  // display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  paddingRight: '20px',
  height: '100%',
  text: 'right',
};

interface FormInputProps {
  label: string;
  name: string;
  value: string;
  placeholder?: string;
  required?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  multiline?: boolean;
  rows?: number;
  error?: boolean;
  helperText?: string;
  maxLength?: number;
}

export const FormInput: React.FC<FormInputProps> = ({
  label,
  name,
  value,
  placeholder,
  required = false,
  onChange,
  onBlur,
  multiline = false,
  rows = 1,
  error = false,
  helperText,
  maxLength,
}) => {
  const InputComponent = multiline ? TextField : OutlinedInput;

  const inputStyle = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '4px',
      height: 40,
      width: 600,
      border: 'none',
      ...(multiline && { alignItems: 'start', height: '100px' }),
    },
    '& .MuiOutlinedInput-input': {
      height: '40px',
      padding: '10px 14px',
    },
    ...(multiline && {
      '& .MuiOutlinedInput-inputMultiline': {
        padding: '10px 14px',
        minHeight: `${rows * 24 + 16}px`,
      },
    }),
  };

  const formControlStyle = {
    '& .MuiInputBase-root': {
      fontSize: 14,
      color: 'text.primary',
      width: '400px',
      height: '45px',
    },
    '& .MuiFormLabel-root': {
      fontSize: 14,
    },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        mb: 2,
        minHeight: rows > 1 ? 'auto' : '40px',
      }}
    >
      {label && (
        <Box sx={{ flex: '0 0 200px' }}>
          <Typography sx={labelStyle}>
            {label}
            {required && '*'}:
          </Typography>
        </Box>
      )}
      <Box sx={{ flex: 1, maxWidth: '800px' }}>
        <FormControl fullWidth error={error} sx={formControlStyle}>
          <InputComponent
            name={name}
            placeholder={placeholder || label}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            multiline={multiline}
            rows={rows}
            fullWidth
            sx={inputStyle}
            error={error}
            inputProps={{ maxLength }}
          />
          {(helperText || error) && (
            <FormHelperText error={error}>{helperText}</FormHelperText>
          )}
        </FormControl>
      </Box>
    </Box>
  );
};

interface FormRadioGroupProps {
  label: string;
  name: string;
  value: string;
  options: { value: string; label: string }[];
  required?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  row?: boolean;
  error?: boolean;
  helperText?: string;
}

export const FormRadioGroup: React.FC<FormRadioGroupProps> = ({
  label,
  name,
  value,
  options,
  required = false,
  onChange,
  row = false,
  error = false,
  helperText,
}) => (
  <Box sx={{ display: 'flex', mb: 2, minHeight: '40px', flexDirection: 'row' }}>
    <Box sx={{ flex: '0 0 200px' }}>
      <Typography sx={labelStyle}>
        {label}
        {required && '*'}:
      </Typography>
    </Box>
    <Box
      sx={{
        flex: 1,
        flexWrap: 'wrap',
        flexDirection: 'row',
        alignItems: 'center',
      }}
    >
      <FormControl error={error}>
        <RadioGroup name={name} value={value} onChange={onChange} row={row}>
          {options.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio />}
              label={option.label}
            />
          ))}
        </RadioGroup>
        {helperText && (
          <FormHelperText error={error}>{helperText}</FormHelperText>
        )}
      </FormControl>
    </Box>
  </Box>
);

interface FormFileUploadProps {
  label: string;
  name: string;
  required?: boolean;
  onChange: (file: File | null) => void;
  sizeRequirements?: string;
  error?: boolean;
  helperText?: string;
  value?: File | null;
  isLoading?: boolean;
  url?: string;
}

export const FormFileUpload: React.FC<FormFileUploadProps> = ({
  label,
  name,
  required = false,
  onChange,
  sizeRequirements = 'Min 300*300px & Max 500*500px (Width*Height)',
  error = false,
  helperText,
  value,
  isLoading = false,
  url,
}) => {
  const { error: errorColors } = useThemeColors();
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (value) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(value);
    } else if (url) {
      setPreviewUrl(url);
    } else {
      setPreviewUrl(null);
    }
  }, [value, url]);

  // Determine what text to show for the file status
  const fileStatusText = isLoading
    ? 'Uploading...'
    : value
      ? value.name
      : 'No file chosen';

  return (
    <Box sx={{ display: 'flex', mb: 2, minHeight: '40px' }}>
      {label && (
        <Box sx={{ flex: '0 0 200px' }}>
          <Typography
            sx={{
              ...labelStyle,
              textAlign: 'right', // 👈 this aligns the text to the right
            }}
          >
            {label}
            {required && '*'}:
          </Typography>
        </Box>
      )}
      <Box sx={{ flex: 1 }}>
        <FormControl error={error}>
          <input
            type="file"
            onChange={(event) => {
              onChange(event.currentTarget.files?.[0] || null);
            }}
            style={{ display: 'none' }}
            id={`${name}-upload`}
            accept="image/*"
            disabled={isLoading}
          />
          <Stack spacing={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <label htmlFor={`${name}-upload`}>
                <Button
                  variant="outlined"
                  component="span"
                  disabled={isLoading}
                  sx={{
                    bgcolor: error ? errorColors.light : '#f5f5f5',
                    color: error ? errorColors.main : '#000',
                    textTransform: 'none',
                    height: '35px',
                    borderColor: error ? errorColors.main : 'black',
                    '&:hover': {
                      bgcolor: error ? errorColors.light : '#e0e0e0',
                      borderColor: error ? errorColors.main : 'black',
                    },
                  }}
                >
                  {isLoading ? 'Uploading...' : 'Choose File'}
                </Button>
              </label>
              <Typography variant="body2" color="text.secondary">
                {fileStatusText}
              </Typography>
              {(value || (helperText && !error)) && !isLoading && (
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => onChange(null)}
                  sx={{
                    height: '30px',
                    textTransform: 'uppercase',
                    bgcolor: errorColors.main,
                    '&:hover': {
                      bgcolor: errorColors.dark,
                    },
                  }}
                >
                  Clear
                </Button>
              )}
              {isLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  <Typography variant="body2">Uploading...</Typography>
                </Box>
              )}
            </Box>
            {previewUrl && (
              <Box sx={{ mt: 1 }}>
                <img
                  src={previewUrl}
                  alt="Preview"
                  style={{
                    maxWidth: '200px',
                    maxHeight: '200px',
                    objectFit: 'contain',
                  }}
                />
              </Box>
            )}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  mt: 0.5,
                }}
              >
                {sizeRequirements}
              </Typography>
              {error && helperText && (
                <FormHelperText
                  error
                  sx={{ color: errorColors.main, margin: 0 }}
                >
                  {helperText}
                </FormHelperText>
              )}
            </Box>
          </Stack>
        </FormControl>
      </Box>
    </Box>
  );
};

interface FormSectionProps {
  title: string;
  children: React.ReactNode;
}

export const FormSection: React.FC<FormSectionProps> = ({
  title,
  children,
}) => (
  <Box sx={{ mb: 4 }}>
    <Typography
      variant="h6"
      sx={{
        mb: 2,
        color: '#000',
        borderBottom: '1px solid #e0e0e0',
        paddingBottom: '10px',
        fontWeight: 'bold',
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

interface FormCheckboxProps {
  label: string;
  name: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  error?: boolean;
  helperText?: string;
}

export const FormCheckbox: React.FC<FormCheckboxProps> = ({
  label,
  name,
  checked,
  onChange,
  error = false,
  helperText,
}) => (
  <Box sx={{ display: 'flex', mb: 2, minHeight: '40px' }}>
    <Box sx={{ flex: '0 0 200px' }} />
    <Box sx={{ flex: 1 }}>
      <FormControl error={error}>
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              onChange={(e) => onChange(e.target.checked)}
              name={name}
            />
          }
          label={label}
        />
        {helperText && (
          <FormHelperText error={error}>{helperText}</FormHelperText>
        )}
      </FormControl>
    </Box>
  </Box>
);

interface CurrencyInputProps {
  label: string;
  currency?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
}

export const CurrencyDisplay: React.FC<CurrencyInputProps> = ({
  label,
  currency = '$',
  required = false,
  error = false,
  helperText,
}) => (
  <Box sx={{ display: 'flex', mb: 2, minHeight: '40px' }}>
    <Box sx={{ flex: '0 0 200px' }}>
      <Typography sx={labelStyle}>
        {label}
        {required && '*'}:
      </Typography>
    </Box>
    <Box sx={{ flex: 1 }}>
      <FormControl error={error}>
        <Box sx={{ display: 'flex', gap: '4px', maxWidth: '600px' }}>
          <Box
            sx={{
              width: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: error ? '#ffebee' : '#f5f5f5',
              border: `1px solid ${error ? '#d32f2f' : '#ddd'}`,
              borderRadius: '4px',
              height: '40px',
              color: error ? '#d32f2f' : 'inherit',
            }}
          >
            {currency}
          </Box>
        </Box>
        {helperText && (
          <FormHelperText error={error}>{helperText}</FormHelperText>
        )}
      </FormControl>
    </Box>
  </Box>
);
